import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import { CartLineModel } from "@insite/client-framework/Types/ApiModels";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Img, { ImgProps } from "@insite/mobius/Img";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import React, { useState } from "react";
import { css } from "styled-components";

/* Valtech Custom Code */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductListImgSlideshow from "@store/Components/Store/StoreProductListImgSlideshow";
import { PRODUCT } from "@store/theme/design";
import { imageStyles } from "@store/theme/mixins";

type ProductImageSize = "small" | "medium" | "large";
/* Valtech Custom Code */

interface OwnProps {
    product: CartLineModel | ProductContextModel;
    onClick?: React.EventHandler<React.MouseEvent>;
    extendedStyles?: ProductImageStyles;
    showSmallImage?: boolean;
    // Valtech Custom Code
    size?: ProductImageSize;
}

type Props = OwnProps;

export interface ProductImageStyles {
    linkWrappingImage?: ClickablePresentationProps;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
    // Valtech Custom Code
    wrapper?: InjectableCss;
}

export const productImageStyles: ProductImageStyles = {
    linkWrappingImage: {
        css: css`
            ${imageStyles(PRODUCT.image.ratio)}
            width: 100%;
        `,
    },
    wrapper: {
        css: css`
            width: 100%;

            > span {
                cursor: default;
            }
        `,
    },
    image: {
        css: css`
            height: 100%;
        `,
    },
    img: {
        css: css`
            height: auto;
            width: 100%;
        `,
    },
};

const ProductImage = ({
    product,
    onClick,
    extendedStyles,
    showSmallImage,
    // Valtech Custom Code
    size = "small",
}: Props) => {
    const [styles] = useState(() => mergeToNew(productImageStyles, extendedStyles));

    let altText = "product" in product ? product.product.imageAltText : product.altText;
    if (!altText || altText === "") {
        altText = "product" in product ? product.product.productTitle : product.productName;
    }

    /* Valtech Custom Code */
    const pathProperty = `${size}ImagePath`;
    let imagePath = (product as any).product?.[pathProperty] || (product as any)?.[pathProperty];

    if (!imagePath) {
        imagePath = PRODUCT.image[size]?.defaultPath || "";
        altText = translate("Image Unavailable");
    }
    /* Valtech Custom Code */

    const productDetailPath = "product" in product ? product.productInfo.productDetailPath : product.productUri;
    const loading = product.idx === undefined ? "lazy" : product.idx < 3 ? "eager" : "lazy";

    // Valtech Custom Code
    const productImgGallery = "product" in product ? product.product?.images : undefined;

    /* Valtech Custom Code -> */
    return (
        <StyledWrapper {...styles.wrapper}>
            {productImgGallery && productImgGallery.length > 1 ? (
                <ProductListImgSlideshow
                    imageCollection={productImgGallery}
                    productDetailPath={productDetailPath}
                    imgLoading={loading}
                    onClick={onClick}
                />
            ) : (
                <Clickable
                    {...styles.linkWrappingImage}
                    href={productDetailPath}
                    data-test-selector="productImage"
                    onClick={onClick}
                >
                    <Img {...styles.img} loading={loading} src={imagePath} altText={altText} />
                </Clickable>
            )}
        </StyledWrapper>
    );
    /* <- Valtech Custom Code */
};

export default ProductImage;
